import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Link,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { getMatchConfidenceLevel } from '../../utils/companySecretaryImportTemplate';

const CompanySecretaryDuplicateResolutionPanel = ({ open, secretary, onClose, onResolve }) => {
  const [selectedAction, setSelectedAction] = useState('');

  const handleResolve = () => {
    if (!selectedAction) return;

    onResolve({
      secretaryIndex: secretary._rowIndex,
      action: selectedAction,
      existingSecretaryId: selectedAction.startsWith('map-')
        ? selectedAction.replace('map-', '')
        : null,
    });
  };

  const renderSecretaryInfo = (data, title, isImported = false) => (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: isImported ? '#f0f7ff' : '#f5f5f5' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {(data.firstName || data.name) && (
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim()}
            </Typography>
          </Box>
        )}
        {data.email && (
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.email}</Typography>
          </Box>
        )}
        {data.phone && (
          <Box display="flex" alignItems="center" gap={1}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.phone}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const renderMatchOption = (match, index) => {
    const confidence = getMatchConfidenceLevel(match.score);

    return (
      <Box key={match.id} sx={{ mb: 2 }}>
        <FormControlLabel
          value={`map-${match.id}`}
          control={<Radio />}
          label={(
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight={500}>Match #{index + 1}</Typography>
              <Chip label={confidence.label} size="small" color={confidence.color} sx={{ height: 20, fontSize: '0.7rem' }} />
              <Chip label={`${match.score}% match`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Box>
          )}
          sx={{ alignItems: 'flex-start', mb: 1 }}
        />
        <Box sx={{ ml: 4 }}>
          {renderSecretaryInfo(match, '', false)}
          {match.id && (
            <Link href={`/company-secretaries/${match.id}`} target="_blank" variant="caption">
              View Account →
            </Link>
          )}
        </Box>
      </Box>
    );
  };

  if (!secretary) return null;

  const hasMatches = secretary.matches && secretary.matches.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '90vh', maxHeight: '900px' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Resolve {secretary.status === 'duplicate' ? 'Duplicate' : 'Error'}
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {renderSecretaryInfo(secretary, `Imported Row #${secretary._rowIndex + 1}`, true)}

        {secretary.errors?.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {secretary.errors.map((error, idx) => (
                <li key={idx}><Typography variant="body2">{error}</Typography></li>
              ))}
            </ul>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Resolution Options</Typography>

        <RadioGroup value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)}>
          {hasMatches && secretary.matches.map((match, index) => renderMatchOption(match, index))}
          {hasMatches && <Divider sx={{ my: 2 }} />}

          <FormControlLabel
            value="create-new"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>Create as New Account</Typography>
                <Typography variant="caption" color="textSecondary">May fail if email already exists</Typography>
              </Box>
            )}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            value="skip"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>Skip This Entry</Typography>
                <Typography variant="caption" color="textSecondary">Do not import this row</Typography>
              </Box>
            )}
          />
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={handleResolve} disabled={!selectedAction}>Apply Resolution</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanySecretaryDuplicateResolutionPanel;
